const rawApiUrl = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');
const API_BASE = rawApiUrl.endsWith('/api') ? rawApiUrl : `${rawApiUrl}/api`;
const TOKEN_KEY = 'ppm_auth_token';

function authHeaders(isJson = false) {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = {};
  if (isJson) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function parseJson(res) {
  if (res.status === 204) return null;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Request failed: ${res.status}`);
  }
  return data;
}

async function apiGet(path) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: authHeaders(),
  });
  return parseJson(res);
}

async function apiMutate(path, method, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: authHeaders(true),
    body: JSON.stringify(body),
  });
  return parseJson(res);
}

export async function login(username, password) {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: authHeaders(true),
    body: JSON.stringify({ username, password }),
  });
  const data = await parseJson(res);
  localStorage.setItem(TOKEN_KEY, data.token);
  return data.user;
}

export function clearAuthToken() {
  localStorage.removeItem(TOKEN_KEY);
}

export async function fetchMe() {
  return apiGet('/auth/me');
}

export async function fetchUsers() {
  return apiGet('/users');
}

export async function createUser(user) {
  return apiMutate('/users', 'POST', user);
}

export async function updateUser(id, user) {
  return apiMutate(`/users/${id}`, 'PUT', user);
}

export async function deleteUser(id) {
  const res = await fetch(`${API_BASE}/users/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return parseJson(res);
}

export async function fetchProjects() {
  return apiGet('/projects');
}

export async function createProject(project) {
  return apiMutate('/projects', 'POST', project);
}

export async function deleteProject(id) {
  const res = await fetch(`${API_BASE}/projects/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function updateProject(id, project) {
  return apiMutate(`/projects/${id}`, 'PUT', project);
}

export async function fetchPrograms() {
  return apiGet('/programs');
}

export async function createProgram(program) {
  return apiMutate('/programs', 'POST', program);
}

export async function deleteProgram(id) {
  const res = await fetch(`${API_BASE}/programs/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function updateProgram(id, program) {
  return apiMutate(`/programs/${id}`, 'PUT', program);
}

export async function fetchInitiatives() {
  return apiGet('/initiatives');
}

export async function createInitiative(initiative) {
  return apiMutate('/initiatives', 'POST', initiative);
}

export async function deleteInitiative(id) {
  const res = await fetch(`${API_BASE}/initiatives/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function updateInitiative(id, initiative) {
  return apiMutate(`/initiatives/${id}`, 'PUT', initiative);
}

export async function fetchRisks() {
  return apiGet('/risks');
}

export async function createRisk(risk) {
  return apiMutate('/risks', 'POST', risk);
}

export async function deleteRisk(id) {
  const res = await fetch(`${API_BASE}/risks/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function updateRisk(id, risk) {
  return apiMutate(`/risks/${id}`, 'PUT', risk);
}

export async function fetchDocuments() {
  return apiGet('/documents');
}

export async function createDocument(document) {
  return apiMutate('/documents', 'POST', document);
}

export async function deleteDocument(id) {
  const res = await fetch(`${API_BASE}/documents/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function updateDocument(id, document) {
  return apiMutate(`/documents/${id}`, 'PUT', document);
}

export async function fetchFinances() {
  return apiGet('/finances');
}

export async function createFinance(finance) {
  return apiMutate('/finances', 'POST', finance);
}

export async function updateFinance(id, finance) {
  return apiMutate(`/finances/${id}`, 'PUT', finance);
}

export async function deleteFinance(id) {
  const res = await fetch(`${API_BASE}/finances/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function fetchDependencies() {
  return apiGet('/dependencies');
}

export async function createDependency(dependency) {
  return apiMutate('/dependencies', 'POST', dependency);
}

export async function updateDependency(id, dependency) {
  return apiMutate(`/dependencies/${id}`, 'PUT', dependency);
}

export async function deleteDependency(id) {
  const res = await fetch(`${API_BASE}/dependencies/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function fetchSlas() {
  return apiGet('/slas');
}

export async function createSla(sla) {
  return apiMutate('/slas', 'POST', sla);
}

export async function updateSla(id, sla) {
  return apiMutate(`/slas/${id}`, 'PUT', sla);
}

export async function deleteSla(id) {
  const res = await fetch(`${API_BASE}/slas/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function fetchChangeWorkflow() {
  return apiGet('/change-workflow');
}

export async function updateChangeWorkflow(config) {
  return apiMutate('/change-workflow', 'PUT', config);
}

export async function fetchChanges() {
  return apiGet('/changes');
}

export async function createChange(changeRequest) {
  return apiMutate('/changes', 'POST', changeRequest);
}

export async function updateChange(id, changeRequest) {
  return apiMutate(`/changes/${id}`, 'PUT', changeRequest);
}

export async function deleteChange(id) {
  const res = await fetch(`${API_BASE}/changes/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function fetchApprovals() {
  return apiGet('/approvals');
}

export async function createApproval(approval) {
  return apiMutate('/approvals', 'POST', approval);
}

export async function updateApproval(id, approval) {
  return apiMutate(`/approvals/${id}`, 'PUT', approval);
}

export async function deleteApproval(id) {
  const res = await fetch(`${API_BASE}/approvals/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function fetchBenefits() {
  return apiGet('/benefits');
}

export async function createBenefit(benefit) {
  return apiMutate('/benefits', 'POST', benefit);
}

export async function deleteBenefit(id) {
  const res = await fetch(`${API_BASE}/benefits/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function updateBenefit(id, benefit) {
  return apiMutate(`/benefits/${id}`, 'PUT', benefit);
}

export async function fetchAudits() {
  return apiGet('/audits');
}

export async function createAudit(audit) {
  return apiMutate('/audits', 'POST', audit);
}

export async function deleteAudit(id) {
  const res = await fetch(`${API_BASE}/audits/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function updateAudit(id, audit) {
  return apiMutate(`/audits/${id}`, 'PUT', audit);
}

export async function fetchIncidents() {
  return apiGet('/incidents');
}

export async function createIncident(incident) {
  return apiMutate('/incidents', 'POST', incident);
}

export async function deleteIncident(id) {
  const res = await fetch(`${API_BASE}/incidents/${id}`, { method: 'DELETE', headers: authHeaders() });
  return parseJson(res);
}

export async function updateIncident(id, incident) {
  return apiMutate(`/incidents/${id}`, 'PUT', incident);
}

export async function fetchAuditLogs() {
  return apiGet('/audit-logs');
}

export async function fetchEntity(type, id) {
  if (!type || !id) return null;
  const t = String(type).toLowerCase();
  const map = {
    project: 'projects',
    program: 'programs',
    initiative: 'initiatives',
    risk: 'risks',
    document: 'documents',
    dependency: 'dependencies',
    sla: 'slas',
    approval: 'approvals',
    benefit: 'benefits',
    audit: 'audits',
    incident: 'incidents',
    // Spanish variants
    proyecto: 'projects',
    programa: 'programs',
    iniciativa: 'initiatives',
    riesgo: 'risks',
    documento: 'documents',
    dependencia: 'dependencies',
    dependencias: 'dependencies',
    sla: 'slas',
    aprobacion: 'approvals',
    aprobación: 'approvals',
    beneficio: 'benefits',
    auditoría: 'audits',
    auditoria: 'audits',
    incidente: 'incidents',
  };
  const plural = map[t] || `${t}s`;
  try {
    return await apiGet(`/${plural}/${id}`);
  } catch (_err) {
    return null;
  }
}

export async function fetchStats() {
  return apiGet('/stats');
}
